<template>
  <div
    v-if="item?.product"
    data-test-id="cart-item"
    :class="[
      $style['cart-item'],
      { [$style['cart-item--compact']]: compact },
      { [$style['cart-item--no-border']]: noBorder }
    ]">
    <div v-if="!noImage" :class="$style['cart-item__image']">
      <img :src="imageUrl">
    </div>

    <div :class="$style['cart-item__content']">
      <grid>
        <cell size="9">
          <div v-if="item.product.tags.includes('hiddengoodie')">
            <b>{{ item.product.title }}</b>
          </div>
          <NuxtLink
            v-else
            :no-prefetch="true"
            :to="getProductRoute({product: item.product, lang: $lang})">
            <b>
              <span
                v-if="compact && item.quantity > 1">{{ item.quantity }} x</span>
              {{ item.product.title }}
            </b>
          </NuxtLink>

          <div class="h-color-grey-dark">
            {{ item.product.colorName }}
            {{ item.size }}
          </div>
          <div
            v-if="item.errorMsg"
            class="h-font-size-s">
            {{ item.errorMsg }} (max. {{ item.quantity }})
          </div>
        </cell>

        <cell
          size="3"
          align-end>
          <btn
            icon-only="System/delete-bin-line"
            shape="round"
            size="small"
            title="Delete from cart"
            data-test-id="cart-item-delete"
            @click="remove(item)" />
          <ProductPrice
            v-if="compact"
            class="h-margin-top"
            :price="item.product.price"
            :sale="item.product.originalPrice" />
        </cell>

        <cell
          justify-end
          size="6">
          <FormStepper
            v-if="!compact && !item.product?.tags.includes('hiddengoodie')"
            :model-value="item.quantity"
            type="number"
            min="1"
            @update:model-value="changeQuantity(item, $event)" />
        </cell>

        <cell
          size="6"
          align-end
          justify-end>
          <ProductPrice
            v-if="!compact"
            vertical
            :sale="
              item.product.originalPrice
                ? (
                  parseFloat(item.product.originalPrice.replace(',', '.')) *
                  item.quantity
                )
                  .toFixed(2)
                  .replace('.', ',')
                : false
            "
            :price="
              (parseFloat(item.product.price.replace(',', '.')) * item.quantity)
                .toFixed(2)
                .replace('.', ',')
            " />
        </cell>
      </grid>
    </div>
  </div>
</template>
<script setup>
import getProductRoute from '~/helpers/get-product-route'

const config = useRuntimeConfig()
const { $bus, $t } = useNuxtApp()
const { showDialog } = useDialog()
const cartStore = useCartStore()

const props = defineProps({
  item: {
    type: Object,
    default: null
  },
  compact: {
    type: Boolean,
    default: false
  },
  noBorder: {
    type: Boolean,
    default: false
  },
  noImage: {
    type: Boolean,
    default: false
  }
})

const imageUrl = computed(() => {
  if (!props?.item?.product?.images) { return false }
  return `${config?.public?.cdnURL}pictures/thumbs/${
        props.item.product.images[0]
      }-thumbnail.webp`
})

const remove = cartItem => {
  showDialog(`<b>${cartItem.product.title}</b> ${$t('checkout/remove-from-cart')}`)
    .then(() => {
      cartStore.remove(cartItem)
      $bus.$emit('remove-from-cart', {
        product: cartItem.product,
        size: cartItem.size
      })
    })
    .catch(() => {})
}

const changeQuantity = (cartProduct, value) => {
  cartStore.changeQuantity({
    cartProduct,
    value
  })
}
</script>
<style lang="scss" module>
.cart-item {
  background-color: var(--white);
  border: 1px solid var(--grey-lighter);
  color: var(--black);
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  overflow: hidden;
  position: relative;

  &:not(:first-child) {
    border-top: 0;
  }

  &:first-child {
    border-top-right-radius: var(--border-radius);
    border-top-left-radius: var(--border-radius);
  }

  &:nth-last-child(2) {
    border-bottom-right-radius: var(--border-radius);
    border-bottom-left-radius: var(--border-radius);
  }

  &--compact {
    border: 0;
    border-bottom: 1px solid var(--grey-lighter);
    font-size: var(--font-size-s);
    align-items: flex-start;
  }

  &--no-border {
    border: 0;
  }

  &__message {
    background: var(--white-90-opacity);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    position: absolute;
    inset: 0;
    height: auto;
    width: auto;
  }

  &__image {
    display: flex;
    padding: calc(var(--spacing) * 2);
    width: calc(var(--spacing) * 14);

    .cart-item--compact & {
      padding: calc(var(--spacing) * 2);
      width: calc(var(--spacing) * 12);
      height: auto;
    }

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
    }
  }

  &__content {
    padding: calc(var(--spacing) * 2);
    padding-left: 0;
    flex: 1;

    .grid {
      height: 100%;
    }

    h4 {
      text-transform: none;
    }

    a {
      text-decoration: none;
      font-weight: var(--font-weight-bold);
    }

    .cart-item--compact & {
      padding: 0;
      padding-right: calc(var(--spacing ) * 2);
      padding-top: calc(var(--spacing ) * 2);
    }

    @media (max-width: #{$breakpoint-mobile}) {
      .grid {
        grid-gap: 10px;
      }
    }
  }

  @media screen and (prefers-color-scheme: darker) {
    background-color: var(--grey-darkest);
  }
}
</style>
